import { useState, useCallback, useEffect, useRef } from 'react'
import type { TTSOptions, TTSVoice } from '@/types'

export function useTTS() {
  const [voices, setVoices] = useState<TTSVoice[]>([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [currentText, setCurrentText] = useState('')
  const [selectedVoice, setSelectedVoice] = useState<string>('')
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null)

  useEffect(() => {
    const loadVoices = () => {
      const synthVoices = window.speechSynthesis.getVoices()
      const mapped: TTSVoice[] = synthVoices.map((v, i) => ({
        name: v.name,
        lang: v.lang,
        default: v.default,
        localService: v.localService,
        voiceURI: v.voiceURI,
        gender: v.name.toLowerCase().includes('female') ? 'female' : 
                v.name.toLowerCase().includes('male') ? 'male' : undefined,
      }))
      setVoices(mapped)

      if (!selectedVoice && mapped.length > 0) {
        const defaultVoice = mapped.find(v => v.default) || mapped[0]
        setSelectedVoice(defaultVoice.name)
      }
    }

    loadVoices()

    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices
    }

    return () => {
      window.speechSynthesis.cancel()
    }
  }, [selectedVoice])

  const speak = useCallback((options: TTSOptions) => {
    if (!window.speechSynthesis) {
      throw new Error('Speech synthesis not supported in this browser')
    }

    window.speechSynthesis.cancel()

    const utterance = new SpeechSynthesisUtterance(options.text)
    utterance.rate = options.rate
    utterance.pitch = options.pitch
    utterance.volume = options.volume
    utterance.lang = options.lang

    const voice = voices.find(v => v.name === options.voice)
    if (voice) {
      const synthVoice = window.speechSynthesis.getVoices().find(v => v.name === voice.name)
      if (synthVoice) utterance.voice = synthVoice
    }

    utterance.onstart = () => {
      setIsSpeaking(true)
      setIsPaused(false)
      setCurrentText(options.text)
    }

    utterance.onend = () => {
      setIsSpeaking(false)
      setIsPaused(false)
    }

    utterance.onerror = () => {
      setIsSpeaking(false)
      setIsPaused(false)
    }

    utteranceRef.current = utterance
    window.speechSynthesis.speak(utterance)
  }, [voices])

  const pause = useCallback(() => {
    window.speechSynthesis.pause()
    setIsPaused(true)
  }, [])

  const resume = useCallback(() => {
    window.speechSynthesis.resume()
    setIsPaused(false)
  }, [])

  const stop = useCallback(() => {
    window.speechSynthesis.cancel()
    setIsSpeaking(false)
    setIsPaused(false)
  }, [])

  const getVoicesByLang = useCallback((langCode: string) => {
    return voices.filter(v => v.lang.startsWith(langCode))
  }, [voices])

  return {
    voices,
    isSpeaking,
    isPaused,
    currentText,
    selectedVoice,
    setSelectedVoice,
    speak,
    pause,
    resume,
    stop,
    getVoicesByLang,
  }
}
